import { useState, useEffect } from 'react';
import {
  Zap, TrendingUp, TrendingDown, Plus, Trash2, AlertTriangle, Loader2,
  CheckCircle, RefreshCw, X, Target, Shield, Activity,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useActivePolling } from '../hooks/useActivePolling';
import { InfoTip } from '../components/ui';
import TradingViewChart from '../components/TradingViewChart';
import DriftBanner from '../components/DriftBanner';
import {
  listSmartTrades, getSmartTrade, getSmartTradeStats, openSmartTrade,
  closeSmartTrade, syncSmartTrade, getExchangeConnections, getExchangeMarkets,
} from '../services/api';

// Fallback list used only during the brief window before the real market list
// loads from the selected exchange. Each exchange has a different real-pair set
// (Binance US has no BUSD pairs, Kraken has EUR/GBP quotes, etc.) — we always
// prefer the live list once it's fetched.
const FALLBACK_PAIRS = ['BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'BNB/USDT'];

// Sort pairs so popular bases/quotes surface first, then alphabetic — same
// ordering helper used on the CEX Trading page.
function sortPairs(pairs) {
  const POPULAR_BASES  = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'DOGE', 'ADA', 'AVAX', 'LINK', 'MATIC', 'DOT', 'UNI', 'ATOM', 'NEAR', 'APT', 'ARB', 'OP'];
  const POPULAR_QUOTES = ['USDT', 'USDC', 'USD', 'BUSD', 'DAI'];
  const rank = (p) => {
    const [base, quote] = p.split('/');
    const baseRank  = POPULAR_BASES.indexOf(base);
    const quoteRank = POPULAR_QUOTES.indexOf(quote);
    return (quoteRank === -1 ? 100 : quoteRank) * 1000 + (baseRank === -1 ? 999 : baseRank);
  };
  return [...pairs].sort((a, b) => {
    const ra = rank(a), rb = rank(b);
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b);
  });
}
const DEFAULT_TPS = [
  { pricePct: 2,  sizePct: 25 },
  { pricePct: 5,  sizePct: 35 },
  { pricePct: 10, sizePct: 40 },
];

// Starter ladder presets — one-click load to skip manual math for first-time users.
const LADDER_PRESETS = [
  {
    id: 'conservative',
    label: 'Conservative 50/30/20',
    desc: 'Bank early. 50% at +1.5%, 30% at +3%, 20% at +6%. SL 2%.',
    apply: {
      takeProfits: [
        { pricePct: 1.5, sizePct: 50 },
        { pricePct: 3,   sizePct: 30 },
        { pricePct: 6,   sizePct: 20 },
      ],
      stopLossPct: 2,
    },
  },
  {
    id: 'balanced',
    label: 'Balanced 25/35/40 (default)',
    desc: 'Bank some early, ride a bit. 25% at +2%, 35% at +5%, 40% trailing at +10%. SL 3%.',
    apply: {
      takeProfits: [
        { pricePct: 2,  sizePct: 25 },
        { pricePct: 5,  sizePct: 35 },
        { pricePct: 10, sizePct: 40, trailing: true, trailingPct: 3 },
      ],
      stopLossPct: 3,
    },
  },
  {
    id: 'runner',
    label: 'Runner 20/20/60 trailing',
    desc: 'For breakouts. 20% at +3%, 20% at +7%, 60% trailing at +15%. SL 4%.',
    apply: {
      takeProfits: [
        { pricePct: 3,  sizePct: 20 },
        { pricePct: 7,  sizePct: 20 },
        { pricePct: 15, sizePct: 60, trailing: true, trailingPct: 5 },
      ],
      stopLossPct: 4,
    },
  },
];

function formatNum(v, dp = 2) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

export default function SmartTradePage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('open'); // 'open' | 'positions'
  const [connectedExchanges, setConnectedExchanges] = useState([]);
  const [loadingExchanges, setLoadingExchanges] = useState(true);

  const [config, setConfig] = useState({
    exchangeId: '',
    symbol: 'BTC/USDT',
    side: 'long',
    entryType: 'market',
    entryPrice: '',
    amount: '',
    name: '',
    trailingBuy: false,
    trailingBuyTriggerPct: 1.5,
    takeProfits: DEFAULT_TPS,
    stopLoss: { pricePct: 3, trailing: false, trailingPct: 2, moveToBreakEvenAfterFirstTP: false },
  });
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState(null);
  const [openSuccess, setOpenSuccess] = useState(null);

  const [trades, setTrades] = useState([]);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [tradeDetails, setTradeDetails] = useState(null);
  const [tradeStats, setTradeStats] = useState(null);
  const [busy, setBusy] = useState(false);

  // Per-exchange real market list, fetched from CCXT loadMarkets. Different
  // exchanges support different pairs — Binance US has ~300, Coinbase ~600, etc.
  // Pre-populated with a small fallback so the dropdown isn't empty during the
  // brief window before the first fetch returns.
  const [pairs, setPairs] = useState(FALLBACK_PAIRS);
  const [pairsLoading, setPairsLoading] = useState(false);
  const [pairSearch, setPairSearch] = useState('');

  // Load connected exchanges. Backend returns { exchanges: [...] } — each entry
  // has a `connected: bool` flag. We filter to only the truly-connected ones so
  // the dropdown matches CEX Trading's behavior (the rest are shown as "Add in
  // Settings → Connections" empty state).
  useEffect(() => {
    (async () => {
      try {
        const res = await getExchangeConnections();
        const all = res?.exchanges || res?.data?.exchanges || [];
        const conns = all.filter(e => e.connected);
        setConnectedExchanges(conns);
        if (conns.length && !config.exchangeId) setConfig(c => ({ ...c, exchangeId: conns[0].exchangeId }));
      } catch (err) { console.error(err); }
      finally { setLoadingExchanges(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTrades = async () => {
    try {
      const res = await listSmartTrades();
      setTrades(res?.data || []);
    } catch (err) { console.error(err); }
  };
  useEffect(() => { if (activeTab === 'positions') loadTrades(); }, [activeTab]);

  // Fetch the real market list whenever the selected exchange changes. Cached
  // 1 hour per exchange on the backend so repeated switches are cheap. Auto-
  // resets the chosen symbol if it doesn't exist on the new exchange.
  useEffect(() => {
    if (!config.exchangeId) return;
    let cancelled = false;
    setPairsLoading(true);
    setPairSearch('');
    getExchangeMarkets(config.exchangeId)
      .then(res => {
        if (cancelled) return;
        const all = (res?.markets || []).map(m => m.symbol);
        if (all.length === 0) { setPairs(FALLBACK_PAIRS); return; }
        const sorted = sortPairs(all);
        setPairs(sorted);
        if (!sorted.includes(config.symbol)) {
          setConfig(c => ({ ...c, symbol: sorted[0] }));
        }
      })
      .catch(() => { if (!cancelled) setPairs(FALLBACK_PAIRS); })
      .finally(() => { if (!cancelled) setPairsLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.exchangeId]);

  // Filtered list for the searchable dropdown (substring match, cap 100 for UI sanity)
  const filteredPairs = pairSearch
    ? pairs.filter(p => p.toLowerCase().includes(pairSearch.toLowerCase())).slice(0, 100)
    : pairs.slice(0, 100);

  // Active polling on positions tab — 5s (faster than grid because trailing SL changes fast)
  const liveInterval = useActivePolling(5000, activeTab === 'positions');
  useEffect(() => {
    if (!liveInterval) return;
    const id = setInterval(loadTrades, liveInterval);
    return () => clearInterval(id);
  }, [liveInterval]);

  useEffect(() => {
    if (!selectedTrade) { setTradeDetails(null); setTradeStats(null); return; }
    (async () => {
      try {
        const [d, s] = await Promise.all([
          getSmartTrade(selectedTrade.tradeId),
          getSmartTradeStats(selectedTrade.tradeId),
        ]);
        setTradeDetails(d?.data);
        setTradeStats(s?.data);
      } catch (err) { console.error(err); }
    })();
  }, [selectedTrade]);

  // Validation: TP sizes must total 100
  const tpSizeTotal = config.takeProfits.reduce((s, tp) => s + (parseFloat(tp.sizePct) || 0), 0);
  const tpSizeValid = Math.abs(tpSizeTotal - 100) < 0.01;

  function updateTp(idx, field, value) {
    // Booleans pass through; numerics get parsed
    const coerced = typeof value === 'boolean' ? value : (parseFloat(value) || 0);
    setConfig(c => ({
      ...c,
      takeProfits: c.takeProfits.map((tp, i) => i === idx ? { ...tp, [field]: coerced } : tp),
    }));
  }
  function addTp() {
    setConfig(c => {
      if (c.takeProfits.length >= 5) return c;
      const last = c.takeProfits[c.takeProfits.length - 1] || { pricePct: 5, sizePct: 0 };
      return { ...c, takeProfits: [...c.takeProfits, { pricePct: last.pricePct + 5, sizePct: 10 }] };
    });
  }
  function removeTp(idx) {
    setConfig(c => ({ ...c, takeProfits: c.takeProfits.filter((_, i) => i !== idx) }));
  }

  async function handleOpen() {
    setOpenError(null); setOpenSuccess(null); setOpening(true);
    try {
      const payload = {
        ...config,
        amount: parseFloat(config.amount),
        entryPrice: config.entryType === 'limit' ? parseFloat(config.entryPrice) : null,
        takeProfits: config.takeProfits.map(tp => ({
          pricePct: parseFloat(tp.pricePct),
          sizePct: parseFloat(tp.sizePct),
          trailing: !!tp.trailing,
          trailingPct: tp.trailing ? (parseFloat(tp.trailingPct) || 1) : undefined,
        })),
      };
      const res = await openSmartTrade(payload);
      setOpenSuccess(res?.data?.trade);
      setActiveTab('positions');
      await loadTrades();
    } catch (err) {
      setOpenError(err.message);
    } finally {
      setOpening(false);
    }
  }

  async function handleClose(tradeId) {
    if (!confirm('Close this position? Cancels all open orders and submits a market order to flatten.')) return;
    setBusy(true);
    try {
      await closeSmartTrade(tradeId);
      await loadTrades();
      const s = await getSmartTradeStats(tradeId);
      setTradeStats(s?.data);
    } catch (err) {
      alert('Close failed: ' + err.message);
    } finally { setBusy(false); }
  }

  async function handleSync(tradeId) {
    setBusy(true);
    try {
      await syncSmartTrade(tradeId);
      const [d, s] = await Promise.all([getSmartTrade(tradeId), getSmartTradeStats(tradeId)]);
      setTradeDetails(d?.data);
      setTradeStats(s?.data);
    } catch (err) {
      alert('Sync failed: ' + err.message);
    } finally { setBusy(false); }
  }

  const canOpen =
    config.exchangeId && config.symbol && config.side && config.entryType &&
    parseFloat(config.amount) > 0 &&
    (config.entryType === 'market' || parseFloat(config.entryPrice) > 0) &&
    config.takeProfits.length > 0 && tpSizeValid &&
    !opening;

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl">
      <header>
        <div className="flex items-center gap-3 mb-2">
          <Zap className="w-7 h-7 text-cyan-400" />
          <h1 className="text-2xl font-bold text-white">Smart Trade Terminal</h1>
          <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-400 border border-cyan-500/30">Starter+</span>
        </div>
        <p className="text-sm text-gray-400">Entry + up to 5 take-profits + trailing stop-loss as one logical position. <span className="text-emerald-400">Real CCXT execution. No simulation.</span></p>
      </header>

      <div className="border-b border-dark-700/50 flex gap-1">
        <button onClick={() => setActiveTab('open')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'open' ? 'border-cyan-400 text-white' : 'border-transparent text-gray-500 hover:text-white'}`}>
          Open Position
        </button>
        <button onClick={() => setActiveTab('positions')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'positions' ? 'border-cyan-400 text-white' : 'border-transparent text-gray-500 hover:text-white'}`}>
          Positions {trades.length > 0 && <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded bg-dark-700 text-gray-400">{trades.length}</span>}
        </button>
      </div>

      {activeTab === 'open' && (
        <>
          <TradingViewChart symbol={config.symbol} exchangeId={config.exchangeId} interval="60" height={420} />
          <OpenPanel
          config={config} setConfig={setConfig} updateTp={updateTp} addTp={addTp} removeTp={removeTp}
          tpSizeTotal={tpSizeTotal} tpSizeValid={tpSizeValid}
          connectedExchanges={connectedExchanges} loadingExchanges={loadingExchanges}
          pairs={pairs} filteredPairs={filteredPairs} pairsLoading={pairsLoading}
          pairSearch={pairSearch} setPairSearch={setPairSearch}
          opening={opening} openError={openError} openSuccess={openSuccess} onOpen={handleOpen} canOpen={canOpen}
        />
        </>
      )}
      {activeTab === 'positions' && (
        <PositionsPanel
          trades={trades} selectedTrade={selectedTrade} setSelectedTrade={setSelectedTrade}
          details={tradeDetails} stats={tradeStats} busy={busy}
          onClose={handleClose} onSync={handleSync}
        />
      )}
    </div>
  );
}

// ─── Open Panel — 3-column terminal ──────────────────────────────────────────

function OpenPanel({ config, setConfig, updateTp, addTp, removeTp, tpSizeTotal, tpSizeValid, connectedExchanges, loadingExchanges, pairs, filteredPairs, pairsLoading, pairSearch, setPairSearch, opening, openError, openSuccess, onOpen, canOpen }) {
  const set = (k) => (e) => setConfig(c => ({ ...c, [k]: e.target.value }));
  const setSL = (k) => (e) => setConfig(c => ({ ...c, stopLoss: { ...c.stopLoss, [k]: parseFloat(e.target.value) || e.target.value } }));

  function applyPreset(preset) {
    setConfig(c => ({
      ...c,
      takeProfits: preset.apply.takeProfits.map(tp => ({ ...tp })),
      stopLoss: { ...c.stopLoss, pricePct: preset.apply.stopLossPct, moveToBreakEvenAfterFirstTP: true },
    }));
  }

  return (
    <div className="space-y-4">
      {/* Starter presets — one-click loads a TP ladder + matching SL */}
      <div className="bg-dark-800/30 border border-dark-700/40 rounded-xl p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <span className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Start from a preset</span>
          <InfoTip side="right">One-click fills the TP ladder + SL with a sensible default. Also auto-checks "Move SL to break-even after first TP fills." Edit any value after.</InfoTip>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {LADDER_PRESETS.map(p => (
            <button key={p.id} type="button" onClick={() => applyPreset(p)}
              className="text-left bg-dark-800 hover:bg-dark-700 border border-dark-600 hover:border-cyan-400/40 rounded-lg p-2.5 transition-all">
              <div className="text-xs font-semibold text-white">{p.label}</div>
              <div className="text-[10px] text-gray-500 leading-snug mt-0.5">{p.desc}</div>
            </button>
          ))}
        </div>
      </div>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Column 1: Entry */}
      <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-5 space-y-4">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <Target className="w-4 h-4 text-cyan-400" /> Entry
        </h3>

        {loadingExchanges ? (
          <div className="bg-dark-700/50 rounded-lg p-3 text-gray-500 text-sm flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading exchanges…
          </div>
        ) : connectedExchanges.length === 0 ? (
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 text-amber-400 text-xs flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">No exchange connected.</p>
              <p className="text-amber-300 mt-0.5">Add one in <a href="/settings?tab=connections" className="underline">Settings → Connections</a>.</p>
            </div>
          </div>
        ) : (
          <select value={config.exchangeId} onChange={set('exchangeId')}
            className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm">
            {connectedExchanges.map(c => <option key={c.exchangeId} value={c.exchangeId}>{c.name || c.exchangeId}</option>)}
          </select>
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs text-gray-400">Pair</label>
            <span className="text-[10px] text-gray-600">
              {pairsLoading ? 'Loading…' : `${pairs.length.toLocaleString()} on this exchange`}
            </span>
          </div>
          <input
            type="text"
            value={pairSearch}
            onChange={e => setPairSearch(e.target.value)}
            placeholder="Search pair (e.g. BTC, ETH/USDC)"
            className="w-full bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-white text-xs font-mono placeholder-gray-600 focus:outline-none"
          />
          <select value={config.symbol} onChange={set('symbol')}
            size={Math.min(6, Math.max(3, filteredPairs.length))}
            className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-sm font-mono">
            {filteredPairs.length === 0 ? (
              <option disabled>No pairs match "{pairSearch}"</option>
            ) : (
              filteredPairs.map(p => <option key={p} value={p}>{p}</option>)
            )}
          </select>
          {pairs.length > 100 && (
            <p className="text-[10px] text-gray-600">Showing first 100 — refine search to find more</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => setConfig(c => ({ ...c, side: 'long' }))}
            className={`p-2.5 rounded-lg text-sm font-bold border flex items-center justify-center gap-1 transition-colors ${config.side === 'long' ? 'bg-emerald-500/15 border-emerald-400/40 text-emerald-400' : 'bg-dark-700 border-dark-600 text-gray-400'}`}>
            <TrendingUp className="w-4 h-4" /> LONG
          </button>
          <button onClick={() => setConfig(c => ({ ...c, side: 'short' }))}
            className={`p-2.5 rounded-lg text-sm font-bold border flex items-center justify-center gap-1 transition-colors ${config.side === 'short' ? 'bg-red-500/15 border-red-400/40 text-red-400' : 'bg-dark-700 border-dark-600 text-gray-400'}`}>
            <TrendingDown className="w-4 h-4" /> SHORT
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => setConfig(c => ({ ...c, entryType: 'market' }))}
            className={`p-2 rounded-lg text-xs font-medium border ${config.entryType === 'market' ? 'bg-cyan-500/10 border-cyan-500/40 text-cyan-400' : 'bg-dark-700 border-dark-600 text-gray-400'}`}>Market</button>
          <button onClick={() => setConfig(c => ({ ...c, entryType: 'limit' }))}
            className={`p-2 rounded-lg text-xs font-medium border ${config.entryType === 'limit' ? 'bg-cyan-500/10 border-cyan-500/40 text-cyan-400' : 'bg-dark-700 border-dark-600 text-gray-400'}`}>Limit</button>
        </div>

        {config.entryType === 'limit' && (
          <>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">{config.trailingBuy ? 'Anchor Price (price-tracking starts here)' : 'Entry Price'}</label>
              <input type="number" step="any" value={config.entryPrice} onChange={set('entryPrice')}
                className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono" />
            </div>

            <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
              <input type="checkbox" checked={config.trailingBuy}
                onChange={e => setConfig(c => ({ ...c, trailingBuy: e.target.checked }))}
                className="rounded" />
              <span className="flex items-center gap-1.5">
                Trailing buy (chase the dip — fires when price retraces)
                <InfoTip side="right">For LONG: order tracks the LOWEST price and fires market buy when price bounces by the trigger %. For SHORT: tracks HIGHEST and fires when price drops. Useful for "catch the dip without missing the bounce."</InfoTip>
              </span>
            </label>

            {config.trailingBuy && (
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Trigger retrace % (from {config.side === 'long' ? 'lowest' : 'highest'} seen)</label>
                <input type="number" step="0.1" value={config.trailingBuyTriggerPct}
                  onChange={e => setConfig(c => ({ ...c, trailingBuyTriggerPct: parseFloat(e.target.value) || 0 }))}
                  className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono" />
                <p className="text-[10px] text-gray-500 mt-1">
                  {config.side === 'long' ? 'Order tracks the LOWEST price; fires market buy when price bounces by this %' : 'Order tracks the HIGHEST price; fires market sell when price drops by this %'}
                </p>
              </div>
            )}
          </>
        )}

        <div>
          <label className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
            Amount (base units)
            <InfoTip side="right">Amount in BASE asset units, not USD. For BTC/USDT, "0.01" = 0.01 BTC (~$1000 at $100k BTC). For ETH/USDC, "0.01" = 0.01 ETH (~$20 at $2000 ETH). Use a calculator if needed.</InfoTip>
          </label>
          <input type="number" step="any" value={config.amount} onChange={set('amount')} placeholder="0.01"
            className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono" />
        </div>

        <div>
          <label className="text-xs text-gray-400 mb-1 block">Position Name (optional)</label>
          <input value={config.name} onChange={set('name')} placeholder={`${config.side.toUpperCase()} ${config.symbol}`}
            className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm" />
        </div>
      </div>

      {/* Column 2: Take-Profit Ladder */}
      <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-400" /> Take-Profit Ladder
          </h3>
          <button onClick={addTp} disabled={config.takeProfits.length >= 5}
            className="text-xs flex items-center gap-1 text-cyan-400 hover:text-cyan-300 disabled:opacity-30">
            <Plus className="w-3 h-3" /> Add TP
          </button>
        </div>

        <div className="space-y-2">
          {config.takeProfits.map((tp, i) => (
            <div key={i} className="bg-dark-700/30 rounded-lg p-3 border border-dark-600/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-white">TP {i + 1}</span>
                <button onClick={() => removeTp(i)} className="text-gray-500 hover:text-red-400">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-gray-500">Price (% from entry)</label>
                  <input type="number" step="any" value={tp.pricePct} onChange={e => updateTp(i, 'pricePct', e.target.value)}
                    className="w-full bg-dark-800 border border-dark-600 rounded p-1.5 text-white text-sm font-mono" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500">Size (% of position)</label>
                  <input type="number" step="any" value={tp.sizePct} onChange={e => updateTp(i, 'sizePct', e.target.value)}
                    className="w-full bg-dark-800 border border-dark-600 rounded p-1.5 text-white text-sm font-mono" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-[10px] text-gray-300 cursor-pointer mt-2">
                <input type="checkbox" checked={!!tp.trailing} onChange={e => updateTp(i, 'trailing', e.target.checked)} className="rounded" />
                <span className="flex items-center gap-1.5">
                  Trailing TP — let winners run
                  <InfoTip side="top">Instead of exiting at the target, position trails X% from the new peak. Best on the LAST rung only — first rungs at fixed prices to lock profit; last rung trails to capture moonshot upside.</InfoTip>
                </span>
              </label>
              {tp.trailing && (
                <div className="mt-1.5">
                  <label className="text-[10px] text-gray-500">Retrace % from new peak before exit</label>
                  <input type="number" step="0.1" value={tp.trailingPct || 1} onChange={e => updateTp(i, 'trailingPct', e.target.value)}
                    className="w-full bg-dark-800 border border-dark-600 rounded p-1.5 text-white text-sm font-mono" />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className={`text-xs flex items-center justify-between p-2 rounded-lg ${tpSizeValid ? 'bg-emerald-500/5 text-emerald-400' : 'bg-amber-500/5 text-amber-400'}`}>
          <span>Total size:</span>
          <span className="font-bold font-mono">{tpSizeTotal.toFixed(1)}% / 100%</span>
        </div>

        <p className="text-[10px] text-gray-500">
          Tip: a 25/35/40 ladder lets you take partial profits early then ride the rest. After TP 1 fills, the SL can auto-trail to break-even.
        </p>
      </div>

      {/* Column 3: Stop-Loss + Submit */}
      <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-5 space-y-4">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <Shield className="w-4 h-4 text-red-400" /> Stop-Loss
        </h3>

        <div>
          <label className="text-xs text-gray-400 mb-1 flex items-center gap-1.5">
            SL distance (% from entry)
            <InfoTip side="right">% below entry (for longs) where stop-market fires. Pick a chart level where the trade idea is wrong. 2-3% for tight ranges; 4-5% for trending breakouts. Avoid round numbers (3.7% beats 4%).</InfoTip>
          </label>
          <input type="number" step="any" value={config.stopLoss.pricePct} onChange={setSL('pricePct')}
            className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono" />
        </div>

        <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
          <input type="checkbox" checked={config.stopLoss.trailing}
            onChange={e => setConfig(c => ({ ...c, stopLoss: { ...c.stopLoss, trailing: e.target.checked } }))}
            className="rounded" />
          <span className="flex items-center gap-1.5">
            Enable trailing SL
            <InfoTip side="right">SL ratchets UP as price moves favorably (never down). Best for trend-following trades where you want to ride a winner without watching it. Skip when using fixed-rung TPs — they handle exits.</InfoTip>
          </span>
        </label>

        {config.stopLoss.trailing && (
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Trailing distance (% from peak)</label>
            <input type="number" step="any" value={config.stopLoss.trailingPct} onChange={setSL('trailingPct')}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2.5 text-white text-sm font-mono" />
          </div>
        )}

        <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
          <input type="checkbox" checked={config.stopLoss.moveToBreakEvenAfterFirstTP}
            onChange={e => setConfig(c => ({ ...c, stopLoss: { ...c.stopLoss, moveToBreakEvenAfterFirstTP: e.target.checked } }))}
            className="rounded" />
          <span className="flex items-center gap-1.5">
            Move SL to break-even after first TP fills
            <InfoTip side="right">The KILLER feature. The moment TP 1 fills, SL auto-promotes to your entry price — turning the rest into a "free trade" you literally cannot lose money on. Always check this unless you have a specific reason not to.</InfoTip>
          </span>
        </label>

        <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 text-amber-400 text-xs flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>SL is a stop-market order. In fast markets, slippage may exceed the SL distance.</span>
        </div>

        {openError && (
          <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3 text-red-400 text-xs">{openError}</div>
        )}
        {openSuccess && (
          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 text-emerald-400 text-xs flex items-start gap-2">
            <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Position opened: {openSuccess.name}. Check Positions tab.</span>
          </div>
        )}

        <button onClick={onOpen} disabled={!canOpen}
          className="w-full bg-cyan-500 hover:bg-cyan-400 disabled:bg-dark-600 disabled:text-gray-500 text-black font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2">
          {opening ? <><Loader2 className="w-4 h-4 animate-spin" /> Opening…</> : <><Zap className="w-4 h-4" /> Open Smart Trade</>}
        </button>
      </div>
    </div>
    </div>
  );
}

// ─── Positions Panel ─────────────────────────────────────────────────────────

function PositionsPanel({ trades, selectedTrade, setSelectedTrade, details, stats, busy, onClose, onSync }) {
  // Default to showing active positions only. Closed ones pile up over time and
  // 99% of users care about what's live right now, not their history archive.
  const [filter, setFilter] = useState('active'); // 'active' | 'closed' | 'all'

  if (trades.length === 0) {
    return (
      <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-12 text-center">
        <Zap className="w-10 h-10 text-gray-600 mx-auto mb-3" />
        <p className="text-white font-medium mb-1">No positions yet</p>
        <p className="text-sm text-gray-500">Switch to <span className="text-cyan-400">Open Position</span> to place your first smart trade.</p>
      </div>
    );
  }

  const ACTIVE_STATUSES = new Set(['open', 'pending_entry', 'pending_trailing_entry']);
  const activeCount = trades.filter(t => ACTIVE_STATUSES.has(t.status)).length;
  const closedCount = trades.length - activeCount;

  // Sort: active first (any pending/open state), then everything else.
  // Within each group, newest first (by createdAt desc), so recent work is
  // at the top regardless of insertion order from the backend.
  const sorted = [...trades].sort((a, b) => {
    const aActive = ACTIVE_STATUSES.has(a.status) ? 0 : 1;
    const bActive = ACTIVE_STATUSES.has(b.status) ? 0 : 1;
    if (aActive !== bActive) return aActive - bActive;
    const aTime = new Date(a.createdAt || 0).getTime();
    const bTime = new Date(b.createdAt || 0).getTime();
    return bTime - aTime;
  });

  const filtered = filter === 'all'
    ? sorted
    : sorted.filter(t => filter === 'active' ? ACTIVE_STATUSES.has(t.status) : !ACTIVE_STATUSES.has(t.status));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-1 space-y-2">
        {/* Filter tabs — active by default; closed view is opt-in so the
            history archive doesn't clutter the day-to-day view */}
        <div className="inline-flex bg-dark-800 rounded-lg p-0.5 border border-dark-700 mb-1 w-full">
          {[
            { id: 'active', label: 'Active', count: activeCount, color: 'text-emerald-400' },
            { id: 'closed', label: 'Closed', count: closedCount, color: 'text-gray-400' },
            { id: 'all',    label: 'All',    count: trades.length, color: 'text-cyan-400' },
          ].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={`flex-1 px-2 py-1.5 rounded-md text-[10px] font-bold transition-all flex items-center justify-center gap-1.5 ${
                filter === f.id ? `bg-dark-700 ${f.color}` : 'text-gray-500 hover:text-white'
              }`}>
              {f.label}
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${filter === f.id ? 'bg-dark-600' : 'bg-dark-700/50'}`}>{f.count}</span>
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="bg-dark-800/50 rounded-xl border border-dark-700/30 p-6 text-center">
            <p className="text-xs text-gray-500">
              {filter === 'active'
                ? 'No active positions. Open a new one to start.'
                : 'No closed positions yet.'}
            </p>
          </div>
        ) : filtered.map(t => {
          const active = selectedTrade?.tradeId === t.tradeId;
          return (
            <button key={t.tradeId} onClick={() => setSelectedTrade(t)}
              className={`w-full text-left p-4 rounded-xl border transition-all ${active ? 'bg-cyan-500/10 border-cyan-400/40' : 'bg-dark-800/50 border-dark-700/50 hover:border-dark-600'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-bold text-white">{t.name}</span>
                <StatusBadge status={t.status} />
              </div>
              <p className="text-xs text-gray-500 font-mono">{t.exchangeId} · {t.symbol} · <span className={t.side === 'long' ? 'text-emerald-400' : 'text-red-400'}>{t.side.toUpperCase()}</span></p>
              {t.entryPriceFilled && <p className="text-[10px] text-gray-600 mt-1">Entry @ ${formatNum(t.entryPriceFilled, 2)}</p>}
              {t.closedAt && <p className="text-[10px] text-gray-600">Closed {new Date(t.closedAt).toLocaleString()}</p>}
            </button>
          );
        })}
      </div>

      <div className="lg:col-span-2">
        {selectedTrade ? (
          <PositionDetails trade={selectedTrade} details={details} stats={stats} busy={busy} onClose={onClose} onSync={onSync} />
        ) : (
          <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-12 text-center">
            <p className="text-sm text-gray-500">Select a position to see TP ladder, SL status, and fill history.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    open: { label: 'Open', class: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
    pending_entry: { label: 'Pending', class: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
    closed: { label: 'Closed', class: 'bg-gray-500/15 text-gray-400 border-gray-500/30' },
    canceled: { label: 'Canceled', class: 'bg-gray-500/15 text-gray-400 border-gray-500/30' },
  };
  const s = map[status] || { label: status, class: 'bg-dark-700 text-gray-400' };
  return <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${s.class}`}>{s.label}</span>;
}

function PositionDetails({ trade, details, stats, busy, onClose, onSync }) {
  const tpOrders = details?.state?.tpOrders || [];
  const slOrder = details?.state?.slOrder;
  const peakPrice = details?.state?.peakPrice;
  const fills = details?.recentFills || [];

  return (
    <div className="bg-dark-800/50 rounded-2xl border border-dark-700/50 p-5 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-bold text-white">{trade.name}</h3>
          <p className="text-xs text-gray-500 font-mono mt-0.5">
            {trade.exchangeId} · {trade.symbol} · <span className={trade.side === 'long' ? 'text-emerald-400' : 'text-red-400'}>{trade.side.toUpperCase()}</span> · {formatNum(trade.amount, 6)} @ ${formatNum(trade.entryPriceFilled, 2)}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => onSync(trade.tradeId)} disabled={busy} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-dark-600 text-gray-300 hover:border-dark-500 disabled:opacity-50">
            <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} /> Sync
          </button>
          {trade.status === 'open' && (
            <button onClick={() => onClose(trade.tradeId)} disabled={busy} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 disabled:opacity-50">
              <X className="w-3 h-3" /> Close Position
            </button>
          )}
        </div>
      </div>

      {stats && (
        <>
          {stats.drift && (
            <DriftBanner
              drift={stats.drift}
              asset={(trade?.symbol || '').split('/')[0] || 'asset'}
              onReconciled={() => onSync(trade.tradeId)}
            />
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Realized P&L" value={`$${formatNum(stats.realizedPnl, 2)}`} accent={stats.realizedPnl > 0 ? 'emerald' : stats.realizedPnl < 0 ? 'red' : undefined} />
            <Stat label="TPs filled" value={`${stats.tpsFilled}/${stats.tpsTotal}`} />
            <Stat label="SL @" value={stats.slStopPrice ? `$${formatNum(stats.slStopPrice, 2)}` : '—'} />
            <Stat label="Peak price" value={peakPrice ? `$${formatNum(peakPrice, 2)}` : '—'} />
          </div>

          {/* Open exposure — visible while the trade is open, showing the
              REAL at-risk capital and unrealized P&L vs current market. */}
          {stats.openExposure && (
            <div className="rounded-xl border border-amber-500/25 bg-gradient-to-br from-amber-500/5 to-dark-800/40 p-4">
              <div className="flex items-start gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-bold text-amber-300 uppercase tracking-wider">Unrealized open exposure</p>
                  <p className="text-[11px] text-amber-200/70 mt-0.5">
                    This trade is still open — the remaining position is real capital exposed to drawdown until a TP or SL closes it. The "Realized P&L" above only counts TPs that have already fired.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat
                  label="Open qty"
                  value={formatNum(stats.openExposure.remainingQty, 6)}
                  subtext={`entry $${formatNum(stats.openExposure.entryPriceUsd, 2)}`}
                />
                <Stat
                  label="Cost basis"
                  value={`$${formatNum(stats.openExposure.openCostUsd, 2)}`}
                />
                <Stat
                  label="Market value"
                  value={stats.openExposure.openMarketValueUsd != null ? `$${formatNum(stats.openExposure.openMarketValueUsd, 2)}` : '—'}
                  subtext={stats.openExposure.currentPriceUsd != null ? `at $${formatNum(stats.openExposure.currentPriceUsd, 2)}` : 'price unavailable'}
                />
                <Stat
                  label="Unrealized P&L"
                  value={stats.openExposure.unrealizedPnl != null
                    ? `${stats.openExposure.unrealizedPnl >= 0 ? '+' : ''}$${formatNum(stats.openExposure.unrealizedPnl, 2)}`
                    : '—'}
                  accent={stats.openExposure.unrealizedPnl != null
                    ? (stats.openExposure.unrealizedPnl >= 0 ? 'emerald' : 'red')
                    : undefined}
                  subtext={stats.openExposure.unrealizedPnlPct != null
                    ? `${stats.openExposure.unrealizedPnlPct >= 0 ? '+' : ''}${formatNum(stats.openExposure.unrealizedPnlPct, 2)}%`
                    : undefined}
                />
              </div>

              {/* True total P&L panel — combines realized + unrealized so the
                  user sees what they'd actually walk away with right now. */}
              {stats.openExposure.unrealizedPnl != null && (
                <div className="mt-3 bg-dark-900/40 border border-dark-700/40 rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-500">Total if closed now (realized + unrealized)</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">${formatNum(stats.realizedPnl, 2)} realized {stats.openExposure.unrealizedPnl >= 0 ? '+' : '−'} ${formatNum(Math.abs(stats.openExposure.unrealizedPnl), 2)} unrealized</p>
                  </div>
                  <p className={`text-2xl font-bold font-mono ${stats.realizedPnl + stats.openExposure.unrealizedPnl >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                    {stats.realizedPnl + stats.openExposure.unrealizedPnl >= 0 ? '+' : ''}${formatNum(stats.realizedPnl + stats.openExposure.unrealizedPnl, 2)}
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <div>
        <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Take-Profit Ladder</p>
        <div className="space-y-1.5">
          {tpOrders.length === 0 ? (
            <p className="text-xs text-gray-600 italic">TPs will be placed once entry fills.</p>
          ) : tpOrders.map((tp, i) => (
            <div key={i} className="flex items-center justify-between text-xs bg-dark-700/30 rounded-lg p-2 font-mono">
              <span className="text-gray-400">TP {i + 1} (+{tp.pricePct}%, {tp.sizePct}%)</span>
              <span className="text-white">@${formatNum(tp.price, 2)}</span>
              <TpStatus status={tp.status} />
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Stop-Loss</p>
        {!slOrder ? (
          <p className="text-xs text-gray-600 italic">No stop-loss set.</p>
        ) : (
          <div className="flex items-center justify-between text-xs bg-dark-700/30 rounded-lg p-2 font-mono">
            <span className="text-gray-400">{slOrder.trailing ? `Trailing (${slOrder.trailingPct}%)` : `Fixed (${slOrder.pricePct}%)`}</span>
            <span className="text-white">@${formatNum(slOrder.stopPrice, 2)}</span>
            <TpStatus status={slOrder.status} />
          </div>
        )}
      </div>

      <div>
        <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Activity ({fills.length})</p>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {fills.length === 0 ? (
            <p className="text-xs text-gray-600 italic">No activity yet.</p>
          ) : fills.map((f, i) => (
            <div key={i} className="flex items-center justify-between text-xs bg-dark-700/30 rounded-lg p-2 font-mono">
              <span className="text-gray-400">{f.type}</span>
              {f.price && <span className="text-white">@${formatNum(f.price, 2)}</span>}
              <span className="text-gray-600 text-[9px]">{new Date(f.recordedAt).toLocaleTimeString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TpStatus({ status }) {
  const map = {
    open: 'text-cyan-400',
    armed: 'text-amber-400',
    awaiting_trigger: 'text-amber-400',
    trailing: 'text-amber-400',
    filled: 'text-emerald-400',
    canceled: 'text-gray-500',
    superseded: 'text-gray-500',
    rejected: 'text-red-400',
  };
  return <span className={`text-[10px] font-bold uppercase ${map[status] || 'text-gray-500'}`}>{status}</span>;
}

function Stat({ label, value, accent, subtext }) {
  const colorMap = { emerald: 'text-emerald-400', red: 'text-red-400' };
  return (
    <div className="bg-dark-700/30 rounded-lg p-3">
      <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`text-base font-bold font-mono mt-0.5 ${colorMap[accent] || 'text-white'}`}>{value}</p>
      {subtext && <p className="text-[9px] text-gray-500 mt-0.5 font-mono">{subtext}</p>}
    </div>
  );
}
